import { useInfiniteQuery } from "@tanstack/react-query";
import { Shuffle } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getForgotten, getRandom } from "../api";
import { ArtistLinks } from "../components/ArtistLinks";
import { Cover } from "../components/Cover";
import { StationFilter, joinSlugs, parseSlugs } from "../components/StationFilter";
import { ErrorBox, Loading } from "../components/State";
import { TrackRow } from "../components/TrackRow";
import { appearances, longDate, yearRange } from "../format";
import { useTitle } from "../hooks";
import type { ForgottenTrack } from "../types";

const PAGE = 30;
const DECADES = [1960, 1970, 1980, 1990, 2000, 2010, 2020];

export function Discover() {
  useTitle("Zapomniane hity");
  const [sp, setSp] = useSearchParams();
  const filters = {
    chart: sp.get("lista") ?? "",
    exclude: sp.get("bez") ?? "",
    sort: sp.get("sort") ?? "forgotten",
    decade: sp.get("dekada") ?? "",
    min_entries: sp.get("min") ?? "8",
    unseen_years: sp.get("lat") ?? "3",
    top: sp.get("top") ?? "100",
  };
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(sp);
    if (value) next.set(key, value);
    else next.delete(key);
    setSp(next, { replace: true });
  };

  const q = useInfiniteQuery({
    queryKey: ["forgotten", filters],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      getForgotten({ ...filters, limit: PAGE, offset: pageParam }),
    getNextPageParam: (last, all) =>
      last.tracks.length === PAGE ? all.length * PAGE : undefined,
  });
  const tracks = q.data?.pages.flatMap((p) => p.tracks) ?? [];

  const [pick, setPick] = useState<ForgottenTrack | null | undefined>(
    undefined,
  );
  const [rolling, setRolling] = useState(false);
  const roll = async () => {
    setRolling(true);
    try {
      setPick((await getRandom(filters)).track);
    } finally {
      setRolling(false);
    }
  };

  return (
    <>
      <h1>Zapomniane hity</h1>
      <p className="lead">
        Utwory, które długo gościły na listach, a od lat z nich zniknęły.
        Najbardziej „zapomniane” na górze.
      </p>

      <form
        className="filters filters--grid"
        onSubmit={(e) => e.preventDefault()}
      >
        <StationFilter excluded={parseSlugs(filters.exclude)} onChange={(list) => set("bez", joinSlugs(list))} />
        <label>
          Pojawił się w dekadzie
          <select
            value={filters.decade}
            onChange={(e) => set("dekada", e.target.value)}
          >
            <option value="">Dowolnej</option>
            {DECADES.map((d) => (
              <option key={d} value={d}>
                {d}.
              </option>
            ))}
          </select>
        </label>
        <label>
          Nie było go od
          <select
            value={filters.unseen_years}
            onChange={(e) => set("lat", e.target.value)}
          >
            {[1, 3, 5, 10, 15, 20].map((y) => (
              <option key={y} value={y}>
                {y} {y === 1 ? "roku" : "lat"}
              </option>
            ))}
          </select>
        </label>
        <label>
          Min. notowań
          <select
            value={filters.min_entries}
            onChange={(e) => set("min", e.target.value)}
          >
            {[3, 8, 15, 30, 60].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <label>
          Dotarł do
          <select
            value={filters.top}
            onChange={(e) => set("top", e.target.value)}
          >
            <option value="100">dowolnego miejsca</option>
            {[1, 3, 10, 20].map((n) => (
              <option key={n} value={n}>
                TOP {n}
              </option>
            ))}
          </select>
        </label>
        <label>
          Kolejność
          <select value={filters.sort} onChange={(e) => set("sort", e.target.value === "forgotten" ? "" : e.target.value)}>
            <option value="forgotten">Najbardziej zapomniane</option>
            <option value="entries">Najwięcej notowań</option>
            <option value="peak">Najwyższe miejsce</option>
            <option value="oldest">Najdawniej grane</option>
          </select>
        </label>
        <button
          type="button"
          className="primary"
          onClick={roll}
          disabled={rolling}
        >
          <Shuffle size={18} aria-hidden="true" /> Losuj zapomniany hit
        </button>
      </form>

      {pick !== undefined && (
        <aside className="pick" aria-live="polite">
          {pick === null ? (
            <p>Nic nie pasuje do tych filtrów.</p>
          ) : (
            <>
              <Cover
                url={pick.imageUrl}
                id={pick.id}
                title={pick.title}
                size={96}
                play={pick}
              />
              <div>
                <div className="card__kicker">
                  {yearRange(pick.firstDate, pick.lastDate)} · ostatnio{" "}
                  {longDate(pick.lastDate)}
                </div>
                <Link className="pick__title" to={`/utwor/${pick.id}`}>
                  {pick.title}
                </Link>
                <div>
                  <ArtistLinks artists={pick.artists} fallback={pick.credit} />
                </div>
                <div className="muted small">
                  {appearances(pick.entries)} · najwyżej #{pick.bestPosition}
                </div>
              </div>
              <button type="button" className="btn" onClick={roll}>
                Jeszcze raz
              </button>
            </>
          )}
        </aside>
      )}

      {q.isLoading && <Loading />}
      {q.error ? <ErrorBox error={q.error} /> : null}
      {!q.isLoading && tracks.length === 0 && (
        <p className="muted">Nic nie pasuje do tych filtrów, poluzuj któryś.</p>
      )}
      <ol className="list list--numbered">
        {tracks.map((t) => (
          <TrackRow
            key={t.id}
            track={t}
            note={`nie ma od ${Math.floor((Date.now() - Date.parse(t.lastDate)) / 31_557_600_000)} lat`}
          />
        ))}
      </ol>
      {q.hasNextPage && (
        <button
          className="btn more"
          disabled={q.isFetchingNextPage}
          onClick={() => q.fetchNextPage()}
        >
          {q.isFetchingNextPage ? "Ładowanie…" : "Pokaż więcej"}
        </button>
      )}
    </>
  );
}
